import { Component, ElementRef, Input, ViewChild } from "@angular/core";
import { HtmlRenderingService } from "@main/reporting.v3/core/rendering/html-rendering.service";
import { v4 as uuidv4 } from "uuid";


@Component({
    selector: "html-renderer",
    templateUrl: "./html-renderer.component.html",
    styleUrls: ["./html-renderer.component.scss"]
})
export class HtmlRendererComponent {
    // CSS injection settings.
    public readonly cssInjectionScopeId: string = uuidv4();

    // Component fields.
    private _htmlCode: string = null;
    private _cssStyles: string = null;
    private _htmlContainer: ElementRef<HTMLElement>;

    @Input() containerClass: string = "";

    constructor(private htmlRenderingService: HtmlRenderingService) {
    }

    @ViewChild("htmlContainer") protected set htmlContainer(value: ElementRef<HTMLElement>) {
        this._htmlContainer = value;
        this.refreshHtmlContainer(value?.nativeElement).then();
    }

    get htmlContainer(): ElementRef<HTMLElement> {
        return this._htmlContainer;
    }

    @Input() set htmlCode(value: string) {
        this._htmlCode = value ?? "";
        this.refreshHtmlContainer(this.htmlContainer?.nativeElement).then();
    }

    get htmlCode(): string {
        return this._htmlCode;
    }

    @Input() set cssStyles(value: string) {
        this._cssStyles = value ?? "";
        this.refreshHtmlContainer(this.htmlContainer?.nativeElement).then();
    }

    get cssStyles(): string {
        return this._cssStyles;
    }

    get htmlContainerClass(): string {
        return `html-renderer-container html-code-container ${this.containerClass}`
    }


    // Refreshing methods.
    private async refreshHtmlContainer(container: HTMLElement): Promise<void> {
        if (!container) return;

        const document: Document = await this.getHtmlDocument();

        // Replace container children with document children.
        container.replaceChildren(...Array.from(document.body?.childNodes ?? []));
    }

    // Auxiliary methods.
    private async getHtmlDocument(): Promise<Document> {
        if (!this.htmlCode?.length) return new Document();

        // Sanitize html code.
        const html: string = this.htmlRenderingService.sanitizeHtml(this.htmlCode);

        // Create a new node tree.
        const parser: DOMParser = new DOMParser();
        const doc: Document = parser.parseFromString(html, "text/html");

        // Add scoped styles to the document.
        this.htmlRenderingService.insertScopedStyles(this.cssInjectionScopeId, doc.body, this.cssStyles)

        return doc;
    }
}